import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronLeft, ChevronRight, ExternalLink, Flag, MapPinned, ScrollText, Trophy } from 'lucide-react';
import { KWC2026_TRICKS } from '../constants';
import { fetchKwc2026TeamupEntries, saveKwc2026TeamupEntry } from '../storage';
import type { Kwc2026TeamupEntry, Kwc2026TeamupStatus, User } from '../types';
import kwcKendamaRegulationsImage from '../kwc/1.jpg';
import kwcTeamupPosterImage from '../kwc/8.png';
import kwcNoBackupRulesImage from '../kwc/2.png';
import kwcDay2FinalsImage from '../kwc/4.jpg';
import kwcEntryClassesImage from '../kwc/5.jpg';
import kwcNovemberPosterImage from '../kwc/6.png';
import kwcLotteryPrizeImage1 from '../kwc/0771d65958ab5bf78d588482c111f403.jpg';
import kwcLotteryPrizeImage2 from '../kwc/41059800138e390778f72477c92a56aa.jpg';
import kwcLotteryPrizeImage3 from '../kwc/b7def5b7ce00e48927875d459e6f5e3f.jpg';

type Kwc2026ActivityPage = 'hub' | 'tricks' | 'teamup' | 'rules' | 'travel';

interface KWC2026ActivityModeProps {
  user: User | null;
  onBack: () => void;
  onRequireLogin: (message: string) => void;
  page?: Kwc2026ActivityPage;
}

const PAGE_TITLE_MAP: Record<Kwc2026ActivityPage, string> = {
  hub: 'KWC 2026 专栏',
  tricks: 'KWC2026 招式展示',
  teamup: 'KWC 找队友活动',
  rules: 'KWC 规则',
  travel: '签证 / 会场 / 住宿指南'
};

const HUB_FEATURED_CARD_AUTOPLAY_MS = 5000;
const KWC_RULE_POSTER_AUTOPLAY_MS = 5000;

const STATUS_OPTIONS: Array<{ value: Kwc2026TeamupStatus; label: string; description: string }> = [
  { value: 'definitely-going', label: '一定去', description: '已经基本确定去日本现场' },
  { value: 'maybe-going', label: '可能去', description: '在观望预算、时间或签证安排' },
  { value: 'online-watching', label: '线上观战', description: '暂不去现场，但会关注比赛直播' }
];

const KWC_PRELIM_RULE_POINTS = [
  '预选赛会进行 2 轮挑战，每轮限时 3 分钟；每轮都要挑战你自己选择的 5 个招式。',
  '2026 规则没有 2024 年曾使用过的“备选招式”制度，也就是没有 backup tricks。',
  '第 2 轮选择的 5 个招式，必须与第 1 轮的 5 个招式不同。',
  '每一轮的 5 个招式，实际挑战顺序可以自由决定。',
  '每一轮只能使用 1 支剑玉，中途不能更换；但第 2 轮可以换成和第 1 轮不同的剑玉。',
  '每一轮会按照你在限时内成功完成的招式等级来累计得分。',
  '预选赛没有 buzzer beater，必须在时间结束前完整做完招式才算成功。'
] as const;

const KWC_RULE_POSTER_ITEMS = [
  {
    src: kwcNoBackupRulesImage,
    alt: 'KWC 2026 预选赛无备选招式规则海报',
  },
  {
    src: kwcKendamaRegulationsImage,
    alt: 'KWC 2026 Kendama Regulations 尺寸规则海报',
  },
  {
    src: kwcDay2FinalsImage,
    alt: 'KWC 2026 Day 2 决赛等级与加分海报',
  },
  {
    src: kwcEntryClassesImage,
    alt: 'KWC 2026 Day 1 参赛组别海报',
  }
] as const;

const KWC_HUB_INTRO_PARAGRAPHS = [
  'KBT 积分赛模式现已添加 KWC2026 的招式，各位可以开始练习了',
  'WOODONE Kendama World Cup Hatsukaichi 2026（KWC2026）现已开放报名，报名时间 2026 年 8 月 6 日 ~ 2026 年 10 月 12 日，比赛将于 2026 年 11 月 21 日（周六）和 11 月 22 日（周日）举行。',
  '主赛事将在广岛县廿日市市举办，11 月 21 日进行预选赛，11 月 22 日进行决赛。',
  '选手也可以通过 Trial Division 远程参加第一天的预选赛。',
  '决赛选手将从廿日市现场预选赛的优秀选手中选出，并结合外卡选手及种子选手共同组成。',
  '11 月 21 日现场报到时间预计为上午 9:00 至 11:00。开幕式为 11:40，预选赛第 1 组于 12:40 开始。',
  'Trial Division 将于 11 月 21 日上午进行。',
  '参加 Trial Division 的选手不能晋级决赛，也不能获得年龄组奖项。',
  '报名请前往 KWC 官方网站。'
] as const;

const KWC_GLOKEN_ASSET_NOTICE = '相关素材使用已向 GLOKEN 申请使用许可。有关 KWC 所有素材的著作权与商标的权利均归 GLOKEN 所有。';

const KWC_TEAMUP_LOTTERY_PRIZES = [
  { src: kwcLotteryPrizeImage1, alt: '抽奖池奖品图 1', label: 'Dream剑玉枫木套剑' },
  { src: kwcLotteryPrizeImage2, alt: '抽奖池奖品图 2', label: 'ACE Koi榉木单剑' },
  { src: kwcLotteryPrizeImage3, alt: '抽奖池奖品图 3', label: '塔斯T4套剑' }
] as const;

const KWC_LEVEL_BUTTON_STYLES: Record<number, { base: string; selected: string }> = {
  1: {
    base: 'border-white/20 bg-[rgba(216,219,224,0.2)] text-white',
    selected: 'border-white bg-[#bfc4cb] text-white shadow-[0_0_20px_rgba(255,255,255,0.2)]',
  },
  2: {
    base: 'border-[rgba(185,207,223,0.2)] bg-[rgba(185,207,223,0.2)] text-white',
    selected: 'border-white bg-[#92adc0] text-white shadow-[0_0_20px_rgba(185,207,223,0.35)]',
  },
  3: {
    base: 'border-[rgba(144,189,213,0.2)] bg-[rgba(144,189,213,0.2)] text-white',
    selected: 'border-white bg-[#6e9eb7] text-white shadow-[0_0_20px_rgba(144,189,213,0.35)]',
  },
  4: {
    base: 'border-[rgba(138,174,208,0.2)] bg-[rgba(138,174,208,0.2)] text-white',
    selected: 'border-white bg-[#6f93b3] text-white shadow-[0_0_20px_rgba(138,174,208,0.35)]',
  },
  5: {
    base: 'border-[rgba(213,215,159,0.2)] bg-[rgba(213,215,159,0.2)] text-white',
    selected: 'border-white bg-[#b7ba7a] text-white shadow-[0_0_20px_rgba(213,215,159,0.35)]',
  },
  6: {
    base: 'border-[rgba(182,197,143,0.2)] bg-[rgba(182,197,143,0.2)] text-white',
    selected: 'border-white bg-[#97a66f] text-white shadow-[0_0_20px_rgba(182,197,143,0.35)]',
  },
  7: {
    base: 'border-[rgba(155,192,137,0.2)] bg-[rgba(155,192,137,0.2)] text-white',
    selected: 'border-white bg-[#7ca16b] text-white shadow-[0_0_20px_rgba(155,192,137,0.35)]',
  },
  8: {
    base: 'border-[rgba(182,145,191,0.2)] bg-[rgba(182,145,191,0.2)] text-white',
    selected: 'border-white bg-[#936f9b] text-white shadow-[0_0_20px_rgba(182,145,191,0.35)]',
  },
  9: {
    base: 'border-[rgba(222,163,105,0.2)] bg-[rgba(222,163,105,0.2)] text-white',
    selected: 'border-white bg-[#bc8249] text-white shadow-[0_0_20px_rgba(222,163,105,0.35)]',
  },
  10: {
    base: 'border-[rgba(197,114,130,0.2)] bg-[rgba(197,114,130,0.2)] text-white',
    selected: 'border-white bg-[#a45665] text-white shadow-[0_0_20px_rgba(197,114,130,0.35)]',
  },
  11: {
    base: 'border-[rgba(78,166,166,0.2)] bg-[rgba(78,166,166,0.2)] text-white',
    selected: 'border-white bg-[#388787] text-white shadow-[0_0_20px_rgba(78,166,166,0.35)]',
  },
  12: {
    base: 'border-[rgba(94,138,180,0.2)] bg-[rgba(94,138,180,0.2)] text-white',
    selected: 'border-white bg-[#436b91] text-white shadow-[0_0_20px_rgba(94,138,180,0.35)]',
  },
  13: {
    base: 'border-[rgba(123,99,152,0.2)] bg-[rgba(123,99,152,0.2)] text-white',
    selected: 'border-white bg-[#5f4a79] text-white shadow-[0_0_20px_rgba(123,99,152,0.35)]',
  },
  14: {
    base: 'border-[rgba(63,74,90,0.2)] bg-[rgba(63,74,90,0.2)] text-white',
    selected: 'border-white bg-[#2d3541] text-white shadow-[0_0_20px_rgba(63,74,90,0.35)]',
  },
  15: {
    base: 'border-white/20 bg-[rgba(0,0,0,0.2)] text-white',
    selected: 'border-cyan-300 bg-[#111111] text-cyan-100 shadow-[0_0_20px_rgba(34,211,238,0.2)]',
  },
};

const KWC_VIDEO_BASE_URL = (import.meta.env.VITE_KWC_VIDEO_BASE_URL || '').replace(/\/$/, '');

const KWC_LEVEL_VIDEO_FILE_NAMES: Record<number, string> = {
  1: 'level-1.mp4',
  2: 'level-2.mp4',
  3: 'level-3.mp4',
  4: 'level-4.mp4',
  5: 'level-5.mp4',
  6: 'level-6.mp4',
  7: 'level-7.mp4',
  8: 'level-8.mp4',
  9: 'level-9.mp4',
  10: 'level-10.mp4',
  11: 'level-11.mp4',
  12: 'level-12.mp4',
  13: 'level-13.mp4',
  14: 'level-14.mp4',
  15: 'level-15.mp4',
};

const KWC_LEVEL_VIDEO_SOURCE_LINKS: Record<number, string> = {
  1: 'https://www.youtube.com/watch?v=a88rJYgQjmA',
  2: 'https://www.youtube.com/watch?v=ve03LgyJDNk',
  3: 'https://www.youtube.com/watch?v=uuizCC8CW5M',
  4: 'https://www.youtube.com/watch?v=dsqvkWVaN5s',
  5: 'https://www.youtube.com/watch?v=84Uc0awvpa8',
  6: 'https://www.youtube.com/watch?v=l7zGbh3bJk0',
  7: 'https://www.youtube.com/watch?v=JCeEcePT5RI',
  8: 'https://www.youtube.com/watch?v=udj7XabJYNM',
  9: 'https://www.youtube.com/watch?v=-wTAup5aH74',
  10: 'https://www.youtube.com/watch?v=N6HnoElgZm0',
  11: 'https://www.youtube.com/watch?v=ZgZyB3RXzSA',
  12: 'https://www.youtube.com/watch?v=csmVA-LdLtk&t=10s',
  13: 'https://www.youtube.com/watch?v=uA3QL7h7Abk',
  14: 'https://www.youtube.com/watch?v=UeHKo3dvAn4',
  15: 'https://www.youtube.com/watch?v=Kv2ugmS6tWQ',
};

const FEATURED_ENTRY_CARD_ITEMS: Array<{
  page: Extract<Kwc2026ActivityPage, 'tricks' | 'teamup'>;
  titleLines: string[];
  description: string;
  badge: string;
  cardClassName: string;
  labelClassName: string;
  descriptionClassName: string;
  actionClassName: string;
  backgroundImageSrc?: string;
  backgroundOverlayClassName?: string;
}> = [
  {
    page: 'tricks',
    titleLines: ['KWC2026', '招式展示'],
    description: '按等级查看 1-15 级招式内容，方便赛前集中练招。',
    badge: 'Entry',
    cardClassName: 'border-cyan-400/35 bg-sky-950 shadow-[0_0_32px_rgba(34,211,238,0.12)]',
    labelClassName: 'text-cyan-200/80',
    descriptionClassName: 'text-cyan-100/78',
    actionClassName: 'text-cyan-200/85',
    backgroundImageSrc: kwcNovemberPosterImage,
    backgroundOverlayClassName: 'bg-[linear-gradient(145deg,rgba(4,12,26,0.64),rgba(3,10,20,0.74),rgba(4,14,24,0.88))]',
  },
  {
    page: 'teamup',
    titleLines: ['KWC', '找队友活动'],
    description: '看看谁想去 KWC，也可以留下自己的参赛或观赛状态。',
    badge: 'Entry',
    cardClassName: 'border-fuchsia-400/35 bg-fuchsia-950 shadow-[0_0_32px_rgba(217,70,239,0.12)]',
    labelClassName: 'text-fuchsia-200/80',
    descriptionClassName: 'text-fuchsia-100/78',
    actionClassName: 'text-fuchsia-200/85',
    backgroundImageSrc: kwcTeamupPosterImage,
    backgroundOverlayClassName: 'bg-[linear-gradient(145deg,rgba(10,14,40,0.56),rgba(8,12,30,0.7),rgba(16,8,28,0.84))]',
  }
];

const KWC_OFFICIAL_HOME_URL = 'https://kendamaworldcup.com';
const KWC_OFFICIAL_RULES_URL = 'https://storage.googleapis.com/studio-design-asset-files/projects/91aPgKVoql/s-1x1_322c9f41-ed18-48a4-8c6f-f1e236dbd87e.pdf';
const KWC_OFFICIAL_ENTRY_URL = 'https://storage.googleapis.com/studio-design-asset-files/projects/91aPgKVoql/s-1x1_f1a2a24f-6d31-4944-8e1f-99d1a4695527.pdf';
const KWC_INDIVIDUAL_ONSITE_ENTRY_URL = 'https://ws.formzu.net/fgen/S3863108/';
const KWC_INDIVIDUAL_ONLINE_ENTRY_URL = 'https://ws.formzu.net/fgen/S56567338/';
const KWC_TEAM_ENTRY_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSc5tfnj7PYCXo9iZfGeVLyM2XIlHPEhUX2-DqHdqb_85K1ATg/viewform';
const JAPAN_VISA_QA_URL = 'https://www.cn.emb-japan.go.jp/itpr_zh/visa_qa.html';
const JAPAN_VISA_TRAVEL_AGENCY_URL = 'https://www.cn.emb-japan.go.jp/itpr_zh/visa_dantai_daili.html';

const HUB_ACTION_BUTTON_ITEMS: Array<{
  type: 'page' | 'external' | 'signup-modal';
  page?: Extract<Kwc2026ActivityPage, 'rules' | 'travel'>;
  href?: string;
  title: string;
  icon: typeof ScrollText;
}> = [
  {
    type: 'page',
    page: 'rules',
    title: 'KWC 规则',
    icon: BookOpen,
  },
  {
    type: 'signup-modal',
    title: '报名入口',
    icon: ExternalLink,
  }
];

const KWC_SIGNUP_OPTION_ITEMS = [
  {
    title: '个人报名（现场比赛）',
    href: KWC_INDIVIDUAL_ONSITE_ENTRY_URL,
  },
  {
    title: '个人报名（线上比赛）',
    href: KWC_INDIVIDUAL_ONLINE_ENTRY_URL,
  },
  {
    title: '海外选手报名(英文)',
    href: KWC_OFFICIAL_ENTRY_URL,
  },
  {
    title: '团体报名（现场、线上比赛共通）',
    href: KWC_TEAM_ENTRY_URL,
  }
] as const;

const getEntryInitial = (entry: Kwc2026TeamupEntry) => {
  const trimmed = entry.displayName.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : 'K';
};

const getStatusMeta = (status: Kwc2026TeamupStatus) => {
  switch (status) {
    case 'definitely-going':
      return { label: '一定去', className: 'border-emerald-400/30 bg-emerald-500/15 text-emerald-200' };
    case 'maybe-going':
      return { label: '可能去', className: 'border-amber-400/30 bg-amber-500/15 text-amber-200' };
    default:
      return { label: '线上观战', className: 'border-cyan-400/30 bg-cyan-500/15 text-cyan-200' };
  }
};

const KWC2026ActivityMode: React.FC<KWC2026ActivityModeProps> = ({
  user,
  onBack,
  onRequireLogin,
  page = 'hub'
}) => {
  const navigate = useNavigate();
  const hubFeaturedSwipeRef = useRef({
    pointerId: -1,
    startX: 0,
    startY: 0,
    deltaX: 0,
    deltaY: 0,
  });
  const hubFeaturedSuppressClickRef = useRef(false);
  const rulesPosterSwipeRef = useRef({
    pointerId: -1,
    startX: 0,
    startY: 0,
    deltaX: 0,
    deltaY: 0,
  });
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [activeHubFeaturedCardIndex, setActiveHubFeaturedCardIndex] = useState(0);
  const [displayedHubFeaturedCardIndex, setDisplayedHubFeaturedCardIndex] = useState(0);
  const [isHubFeaturedTransitionEnabled, setIsHubFeaturedTransitionEnabled] = useState(true);
  const [activeRulesPosterIndex, setActiveRulesPosterIndex] = useState(0);
  const [entries, setEntries] = useState<Kwc2026TeamupEntry[]>([]);
  const [isEntriesLoading, setIsEntriesLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isTeamupFormOpen, setIsTeamupFormOpen] = useState(false);
  const [teamupStatus, setTeamupStatus] = useState<Kwc2026TeamupStatus>('definitely-going');
  const [teamupNote, setTeamupNote] = useState('');
  const [teamupFilter, setTeamupFilter] = useState<'all' | Kwc2026TeamupStatus>('all');
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [isLotteryOpen, setIsLotteryOpen] = useState(false);
  const [isPrizePoolOpen, setIsPrizePoolOpen] = useState(false);
  const [previewPrize, setPreviewPrize] = useState<typeof KWC_TEAMUP_LOTTERY_PRIZES[number] | null>(null);
  const [lotteryWinner, setLotteryWinner] = useState<Kwc2026TeamupEntry | null>(null);
  const [isLotteryDrawing, setIsLotteryDrawing] = useState(false);
  const lotteryTimerRef = useRef<number | null>(null);

  const trickGroups = useMemo(() => {
    const grouped = new Map<number, typeof KWC2026_TRICKS>();
    KWC2026_TRICKS.forEach((trick) => {
      const list = grouped.get(trick.score) || [];
      list.push(trick);
      grouped.set(trick.score, list);
    });
    return Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]);
  }, []);

  const selectedLevelTricks = useMemo(
    () => trickGroups.find(([level]) => level === selectedLevel)?.[1] || [],
    [selectedLevel, trickGroups]
  );

  const selectedLevelVideoUrl = useMemo(
    () => {
      const fileName = KWC_LEVEL_VIDEO_FILE_NAMES[selectedLevel];
      if (!KWC_VIDEO_BASE_URL || !fileName) return null;
      return `${KWC_VIDEO_BASE_URL}/${fileName}`;
    },
    [selectedLevel]
  );

  const selectedLevelVideoSourceLink = useMemo(
    () => KWC_LEVEL_VIDEO_SOURCE_LINKS[selectedLevel] || null,
    [selectedLevel]
  );

  const myEntry = useMemo(
    () => entries.find((entry) => entry.userId === user?.uid) || null,
    [entries, user?.uid]
  );

  const teamupStats = useMemo(() => ({
    definitelyGoing: entries.filter((entry) => entry.status === 'definitely-going').length,
    maybeGoing: entries.filter((entry) => entry.status === 'maybe-going').length,
    onlineWatching: entries.filter((entry) => entry.status === 'online-watching').length
  }), [entries]);

  const loadEntries = useCallback(async () => {
    setIsEntriesLoading(true);
    const nextEntries = await fetchKwc2026TeamupEntries();
    setEntries(nextEntries);
    setIsEntriesLoading(false);
  }, []);

  useEffect(() => {
    if (page !== 'teamup') return;
    void loadEntries();
  }, [loadEntries, page]);

  useEffect(() => {
    if (!myEntry) return;
    setTeamupStatus(myEntry.status);
    setTeamupNote(myEntry.note);
  }, [myEntry]);

  const openActivityPage = useCallback((nextPage: Exclude<Kwc2026ActivityPage, 'hub'>) => {
    navigate(`/kwc2026/${nextPage}`);
  }, [navigate]);

  const openExternalLink = useCallback((href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  }, []);

  const openSignupModal = useCallback(() => {
    setIsSignupModalOpen(true);
  }, []);

  const closeSignupModal = useCallback(() => {
    setIsSignupModalOpen(false);
  }, []);

  const clearLotteryTimer = useCallback(() => {
    if (lotteryTimerRef.current !== null) {
      window.clearInterval(lotteryTimerRef.current);
      lotteryTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (lotteryTimerRef.current !== null) {
        window.clearInterval(lotteryTimerRef.current);
      }
    };
  }, []);

  const closeLottery = useCallback(() => {
    clearLotteryTimer();
    setIsLotteryOpen(false);
    setIsLotteryDrawing(false);
    setLotteryWinner(null);
  }, [clearLotteryTimer]);

  const startLottery = useCallback(() => {
    if (isLotteryDrawing || entries.length === 0) return;
    clearLotteryTimer();
    setIsLotteryDrawing(true);
    setLotteryWinner(null);

    const pool = entries;
    let tick = 0;
    const totalTicks = 18;
    lotteryTimerRef.current = window.setInterval(() => {
      tick += 1;
      setLotteryWinner(pool[Math.floor(Math.random() * pool.length)]);
      if (tick >= totalTicks) {
        clearLotteryTimer();
        setIsLotteryDrawing(false);
      }
    }, 90);
  }, [clearLotteryTimer, entries, isLotteryDrawing]);

  const scrollRulesPosterToIndex = useCallback((index: number) => {
    const nextIndex = ((index % KWC_RULE_POSTER_ITEMS.length) + KWC_RULE_POSTER_ITEMS.length) % KWC_RULE_POSTER_ITEMS.length;
    setActiveRulesPosterIndex(nextIndex);
  }, []);

  const handleRulesPosterPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (KWC_RULE_POSTER_ITEMS.length <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    rulesPosterSwipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      deltaX: 0,
      deltaY: 0,
    };
  }, []);

  const handleRulesPosterPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (rulesPosterSwipeRef.current.pointerId !== event.pointerId) return;

    rulesPosterSwipeRef.current.deltaX = event.clientX - rulesPosterSwipeRef.current.startX;
    rulesPosterSwipeRef.current.deltaY = event.clientY - rulesPosterSwipeRef.current.startY;
  }, []);

  const handleRulesPosterPointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (rulesPosterSwipeRef.current.pointerId !== event.pointerId) return;

    const { deltaX, deltaY } = rulesPosterSwipeRef.current;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      scrollRulesPosterToIndex(activeRulesPosterIndex + (deltaX < 0 ? 1 : -1));
    }

    rulesPosterSwipeRef.current = {
      pointerId: -1,
      startX: 0,
      startY: 0,
      deltaX: 0,
      deltaY: 0,
    };
  }, [activeRulesPosterIndex, scrollRulesPosterToIndex]);

  useEffect(() => {
    if (page !== 'rules' || KWC_RULE_POSTER_ITEMS.length <= 1) return;

    const timer = window.setTimeout(() => {
      setActiveRulesPosterIndex((prev) => (prev + 1) % KWC_RULE_POSTER_ITEMS.length);
    }, KWC_RULE_POSTER_AUTOPLAY_MS);

    return () => window.clearTimeout(timer);
  }, [activeRulesPosterIndex, page]);

  const hubFeaturedCardCount = FEATURED_ENTRY_CARD_ITEMS.length;
  const hubFeaturedTrackItems = useMemo(
    () => (hubFeaturedCardCount > 1 ? [...FEATURED_ENTRY_CARD_ITEMS, FEATURED_ENTRY_CARD_ITEMS[0]] : FEATURED_ENTRY_CARD_ITEMS),
    [hubFeaturedCardCount]
  );

  const shiftHubFeaturedCard = useCallback((direction: 1 | -1) => {
    if (hubFeaturedCardCount <= 1) return;
    setIsHubFeaturedTransitionEnabled(true);
    setActiveHubFeaturedCardIndex((prev) => {
      if (direction === 1) {
        if (prev === hubFeaturedCardCount - 1) {
          setDisplayedHubFeaturedCardIndex(hubFeaturedCardCount);
          return 0;
        }
        const nextIndex = prev + 1;
        setDisplayedHubFeaturedCardIndex(nextIndex);
        return nextIndex;
      }

      const nextIndex = (prev - 1 + hubFeaturedCardCount) % hubFeaturedCardCount;
      setDisplayedHubFeaturedCardIndex(nextIndex);
      return nextIndex;
    });
  }, [hubFeaturedCardCount]);

  const handleHubFeaturedTrackTransitionEnd = useCallback(() => {
    if (displayedHubFeaturedCardIndex !== hubFeaturedCardCount) return;
    setIsHubFeaturedTransitionEnabled(false);
    setDisplayedHubFeaturedCardIndex(0);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setIsHubFeaturedTransitionEnabled(true);
      });
    });
  }, [displayedHubFeaturedCardIndex, hubFeaturedCardCount]);

  useEffect(() => {
    if (page !== 'hub' || hubFeaturedCardCount <= 1) return;
    const timer = window.setTimeout(() => {
      shiftHubFeaturedCard(1);
    }, HUB_FEATURED_CARD_AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [activeHubFeaturedCardIndex, hubFeaturedCardCount, page, shiftHubFeaturedCard]);

  const handleHubFeaturedPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (hubFeaturedCardCount <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    hubFeaturedSwipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      deltaX: 0,
      deltaY: 0,
    };
  }, [hubFeaturedCardCount]);

  const handleHubFeaturedPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (hubFeaturedSwipeRef.current.pointerId !== event.pointerId) return;
    hubFeaturedSwipeRef.current.deltaX = event.clientX - hubFeaturedSwipeRef.current.startX;
    hubFeaturedSwipeRef.current.deltaY = event.clientY - hubFeaturedSwipeRef.current.startY;
  }, []);

  const handleHubFeaturedPointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (hubFeaturedSwipeRef.current.pointerId !== event.pointerId) return;
    const { deltaX, deltaY } = hubFeaturedSwipeRef.current;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      hubFeaturedSuppressClickRef.current = true;
      shiftHubFeaturedCard(deltaX < 0 ? 1 : -1);
    }
    hubFeaturedSwipeRef.current = {
      pointerId: -1,
      startX: 0,
      startY: 0,
      deltaX: 0,
      deltaY: 0,
    };
  }, [shiftHubFeaturedCard]);

  const handleHubFeaturedCardClick = useCallback((nextPage: Extract<Kwc2026ActivityPage, 'tricks' | 'teamup'>) => {
    if (hubFeaturedSuppressClickRef.current) {
      hubFeaturedSuppressClickRef.current = false;
      return;
    }
    openActivityPage(nextPage);
  }, [openActivityPage]);

  const renderHubFeaturedCard = (
    item: (typeof FEATURED_ENTRY_CARD_ITEMS)[number],
    options?: { className?: string; inert?: boolean; keySuffix?: string }
  ) => {
    const animationClassName = options?.className || '';
    const keySuffix = options?.keySuffix || 'base';

    return (
      <button
        key={`${item.page}-${keySuffix}`}
        type="button"
        onClick={() => handleHubFeaturedCardClick(item.page)}
        aria-hidden={options?.inert}
        tabIndex={options?.inert ? -1 : undefined}
        className={`${animationClassName} relative flex h-full w-full flex-col justify-between overflow-hidden rounded-[26px] border px-[clamp(18px,4vw,24px)] py-[clamp(18px,4vw,24px)] pb-[56px] text-left transition-transform active:scale-[0.985] ${item.cardClassName}`}
        style={item.backgroundImageSrc ? {
          backgroundImage: `url(${item.backgroundImageSrc})`,
          backgroundPosition: 'center 32%',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        } : undefined}
      >
        {item.backgroundImageSrc && (
          <div className={`absolute inset-0 ${item.backgroundOverlayClassName || 'bg-black/40'}`} />
        )}
        <div className="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-white/10 px-3 py-1 font-orbitron text-[10px] uppercase tracking-[0.14em] text-white/90 shadow-lg">
          {item.badge}
        </div>
        <div className="relative z-10">
          <div className={`text-[11px] uppercase tracking-[0.22em] ${item.labelClassName}`}>
            活动入口
          </div>
          <div className="mt-[clamp(18px,3.4vh,28px)] flex flex-col font-orbitron text-[clamp(30px,6.5vw,44px)] font-bold leading-[0.95] tracking-[0.06em] text-white">
            {item.titleLines.map((line, index) => (
              <span key={`${item.page}-${keySuffix}-${index}`} className={index > 0 ? 'mt-1' : ''}>
                {line}
              </span>
            ))}
          </div>
          <div className={`mt-[clamp(12px,2vh,18px)] max-w-[260px] text-[clamp(14px,3vw,16px)] leading-6 ${item.descriptionClassName}`}>
            {item.description}
          </div>
        </div>
        <div className="relative z-10 flex items-center justify-between gap-3">
          <div className={`text-[12px] uppercase tracking-[0.2em] ${item.actionClassName}`}>
            点击进入
          </div>
        </div>
      </button>
    );
  };

  const handleSaveTeamup = useCallback(async () => {
    if (!user) {
      onRequireLogin('请先登录后参加 KWC 找队友活动。');
      return;
    }

    setIsSaving(true);
    setActionMessage(null);
    try {
      const savedEntry = await saveKwc2026TeamupEntry({
        status: teamupStatus,
        note: teamupNote
      });
      setEntries((prev) => {
        const next = [...prev];
        const existingIndex = next.findIndex((entry) => entry.userId === savedEntry.userId);
        if (existingIndex >= 0) {
          next[existingIndex] = savedEntry;
        } else {
          next.unshift(savedEntry);
        }
        return next.sort((a, b) => b.updatedAt - a.updatedAt || b.createdAt - a.createdAt);
      });
      setActionMessage('已加入 KWC 找队友活动。');
      setIsTeamupFormOpen(false);
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : '保存失败，请稍后重试。');
    } finally {
      setIsSaving(false);
    }
  }, [onRequireLogin, teamupNote, teamupStatus, user]);

  const renderHub = () => (
    <div className="space-y-4">
      <div
        className="relative overflow-hidden rounded-[26px]"
        onPointerDown={handleHubFeaturedPointerDown}
        onPointerMove={handleHubFeaturedPointerMove}
        onPointerUp={handleHubFeaturedPointerEnd}
        onPointerCancel={handleHubFeaturedPointerEnd}
      >
        <div
          className={`flex ${isHubFeaturedTransitionEnabled ? 'transition-transform duration-500 ease-out' : ''}`}
          style={{ transform: `translateX(-${displayedHubFeaturedCardIndex * 100}%)` }}
          onTransitionEnd={handleHubFeaturedTrackTransitionEnd}
        >
          {hubFeaturedTrackItems.map((item, index) => (
            <div key={`${item.page}-${index}`} className="min-w-full">
              {renderHubFeaturedCard(item, { keySuffix: `${index}` })}
            </div>
          ))}
        </div>
        {hubFeaturedCardCount > 1 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center gap-2">
            {FEATURED_ENTRY_CARD_ITEMS.map((item, index) => (
              <button
                key={item.page}
                type="button"
                onClick={() => {
                  setIsHubFeaturedTransitionEnabled(true);
                  setActiveHubFeaturedCardIndex(index);
                  setDisplayedHubFeaturedCardIndex(index);
                }}
                className={`pointer-events-auto h-2.5 rounded-full transition-all ${
                  index === activeHubFeaturedCardIndex ? 'w-7 bg-white' : 'w-2.5 bg-white/30'
                }`}
                aria-label={`切换到${item.titleLines.join(' ')}`}
              />
            ))}
          </div>
        )}
      </div>

      <section className="grid grid-cols-2 gap-3">
        {HUB_ACTION_BUTTON_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.title}
              type="button"
              onClick={() => {
                if (item.type === 'page' && item.page) {
                  openActivityPage(item.page);
                  return;
                }
                if (item.type === 'signup-modal') {
                  openSignupModal();
                  return;
                }
                if (item.type === 'external' && item.href) {
                  openExternalLink(item.href);
                }
              }}
              className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm font-medium text-white transition hover:border-white/25 hover:bg-white/[0.09]"
            >
              <Icon className="h-4 w-4" />
              <span>{item.title}</span>
            </button>
          );
        })}
      </section>

      <section className="rounded-[24px] border border-white/10 bg-zinc-950/80 p-5">
        <p className="text-base font-semibold leading-7 text-white sm:text-lg">
          {KWC_HUB_INTRO_PARAGRAPHS[0]}
        </p>
        <div className="space-y-3 text-sm leading-7 text-zinc-200">
          {KWC_HUB_INTRO_PARAGRAPHS.slice(1).map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
        <div className="my-4 h-px bg-white/10" />
        <div className="text-sm leading-7 text-zinc-200">
          <div className="font-medium text-white">WOODONE Kendama World Cup Hatsukaichi 2026</div>
          <a
            href={KWC_OFFICIAL_HOME_URL}
            target="_blank"
            rel="noreferrer"
            className="text-cyan-200 underline decoration-cyan-300/40 underline-offset-4 transition hover:text-cyan-100"
          >
            https://kendamaworldcup.com/en
          </a>
        </div>
        <div className="mt-4 text-sm leading-7 text-zinc-200">期待在 KWC2026 与大家见面！</div>
      </section>

      <section className="rounded-[24px] border border-white/10 bg-zinc-950/80 p-5">
        <div className="text-xl font-semibold text-white">官方入口</div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            { title: 'KWC 官方首页', href: KWC_OFFICIAL_HOME_URL },
            { title: 'KWC 官方规则 PDF', href: KWC_OFFICIAL_RULES_URL },
            { title: 'KWC 官方报名规则', href: KWC_OFFICIAL_ENTRY_URL }
          ].map((item) => (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition hover:border-white/20 hover:bg-white/[0.05]"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-white">{item.title}</div>
                <ExternalLink className="h-4 w-4 text-white/45 transition group-hover:text-white/80" />
              </div>
            </a>
          ))}
        </div>
        <div className="mt-4 text-sm leading-6 text-zinc-400">{KWC_GLOKEN_ASSET_NOTICE}</div>
      </section>
    </div>
  );

  const renderTricks = () => (
    <div className="space-y-4">
      <section className="rounded-[24px] border border-cyan-400/15 bg-zinc-950/80 p-5">
        <div className="text-sm text-cyan-200">等级选择</div>
        <div className="mt-4 grid grid-cols-5 gap-2 md:grid-cols-8 xl:grid-cols-10">
          {trickGroups.map(([level]) => (
            <button
              key={level}
              type="button"
              onClick={() => setSelectedLevel(level)}
              className={`rounded-xl border-2 px-2 py-2 text-xs font-semibold sm:text-sm transition ${
                selectedLevel === level
                  ? KWC_LEVEL_BUTTON_STYLES[level]?.selected || 'border-cyan-300 bg-cyan-400/20 text-cyan-100 shadow-[0_0_20px_rgba(34,211,238,0.18)]'
                  : KWC_LEVEL_BUTTON_STYLES[level]?.base || 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:text-white'
              }`}
            >
              Lv.{level}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-[24px] border border-white/10 bg-zinc-950/80 p-5">
        {selectedLevelVideoUrl && (
          <div className="mb-5 overflow-hidden rounded-2xl border border-cyan-400/15 bg-black/30">
            <video
              key={selectedLevelVideoUrl}
              src={selectedLevelVideoUrl}
              controls
              playsInline
              preload="metadata"
              className="aspect-video w-full bg-black object-contain"
            />
            <div className="border-t border-white/8 bg-white/[0.03] px-4 py-3 text-xs leading-6 text-zinc-300 sm:text-sm">
              <span>视频展示素材已取得 GLOKEN 许可，有关 KWC 所有素材的著作权与商标的权利均归 GLOKEN 所有，视频原地址：</span>
              {selectedLevelVideoSourceLink && (
                <a
                  href={selectedLevelVideoSourceLink}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 break-all text-cyan-200 underline decoration-cyan-300/40 underline-offset-4 transition hover:text-cyan-100"
                >
                  {selectedLevelVideoSourceLink}
                </a>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xl font-semibold text-white">Level {selectedLevel}</div>
            <div className="mt-1 text-sm text-zinc-400">点击上方等级即可切换查看。</div>
          </div>
          <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-100">
            {selectedLevelTricks.length} 招
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {selectedLevelTricks.map((trick) => {
            const lines = trick.name.split('\n');
            const [trickCode, ...englishNameParts] = lines[0].split(' ');
            const englishName = englishNameParts.join(' ') || lines[0];
            return (
              <div key={trick.id} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <div className="text-sm font-semibold tracking-[0.08em] text-fuchsia-100">
                  {trickCode}
                </div>
                <div className="mt-3 text-base font-medium leading-6 text-white">{englishName}</div>
                {lines[1] && <div className="mt-2 text-base leading-6 text-zinc-200">{lines[1]}</div>}
                {lines[2] && (
                  <div className="mt-3 rounded-xl border border-white/8 bg-black/20 px-3 py-2.5 text-base leading-6 text-zinc-300">
                    {lines[2]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );

  const renderTeamup = () => {
    const filteredEntries = teamupFilter === 'all' ? entries : entries.filter((entry) => entry.status === teamupFilter);
    return (
      <div className="space-y-4">
      <section className="grid grid-cols-3 gap-3">
        <div className="rounded-[24px] border border-emerald-400/20 bg-emerald-500/10 p-4">
          <div className="text-sm text-emerald-100/80">一定去</div>
          <div className="mt-2 text-3xl font-semibold text-white">{teamupStats.definitelyGoing}</div>
        </div>
        <div className="rounded-[24px] border border-amber-400/20 bg-amber-500/10 p-4">
          <div className="text-sm text-amber-100/80">可能去</div>
          <div className="mt-2 text-3xl font-semibold text-white">{teamupStats.maybeGoing}</div>
        </div>
        <div className="rounded-[24px] border border-cyan-400/20 bg-cyan-500/10 p-4">
          <div className="text-sm text-cyan-100/80">线上观战</div>
          <div className="mt-2 text-3xl font-semibold text-white">{teamupStats.onlineWatching}</div>
        </div>
      </section>

      <div className="fixed bottom-6 right-6 z-30 flex flex-col items-end gap-2">
        <div className="flex flex-col items-end">
          <div className="rounded-xl border border-fuchsia-300/40 bg-zinc-950/95 px-4 py-2 text-sm font-medium text-fuchsia-100 shadow-[0_0_24px_rgba(217,70,239,0.25)]">
            投票参加抽奖
          </div>
          <div className="mr-8 h-0 w-0 border-x-8 border-t-8 border-x-transparent border-t-fuchsia-300/40" />
        </div>
        {(user?.isAdmin || user?.roles?.includes('administrator')) && (
          <button
            type="button"
            onClick={() => setIsLotteryOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-amber-300/50 bg-amber-500/20 px-5 py-3 text-sm font-medium text-amber-100 shadow-[0_0_24px_rgba(245,158,11,0.3)] backdrop-blur-sm transition hover:bg-amber-500/30"
          >
            <Trophy className="h-4 w-4" />
            抽奖
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (!user) {
              onRequireLogin('请先登录后参加 KWC 找队友活动。');
              return;
            }
            setIsTeamupFormOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-full border border-fuchsia-300/50 bg-fuchsia-500/20 px-5 py-3 text-sm font-medium text-fuchsia-100 shadow-[0_0_24px_rgba(217,70,239,0.3)] backdrop-blur-sm transition hover:bg-fuchsia-500/30"
        >
          参加活动
        </button>
        <button
          type="button"
          onClick={() => setIsPrizePoolOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-sky-300/50 bg-sky-500/20 px-5 py-3 text-sm font-medium text-sky-100 shadow-[0_0_24px_rgba(56,189,248,0.28)] backdrop-blur-sm transition hover:bg-sky-500/30"
        >
          <Trophy className="h-4 w-4" />
          奖池
        </button>
      </div>

      <section className="rounded-[24px] border border-white/10 bg-zinc-950/80 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xl font-semibold text-white">活动列表</div>
          <div className="text-sm text-zinc-400">{filteredEntries.length} 人</div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {([
            { value: 'all' as const, label: '全部' },
            ...STATUS_OPTIONS.map((option) => ({ value: option.value, label: option.label }))
          ]).map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setTeamupFilter(filter.value)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                teamupFilter === filter.value
                  ? 'border-white bg-white/15 text-white'
                  : 'border-white/10 bg-white/[0.03] text-zinc-300 hover:border-white/20 hover:text-white'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-3">
          {isEntriesLoading && <div className="text-sm text-zinc-400">正在加载列表...</div>}
          {!isEntriesLoading && filteredEntries.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-sm text-zinc-400">
              还没有人加入，来当第一个吧。
            </div>
          )}
          {!isEntriesLoading && filteredEntries.map((entry) => {
            const statusMeta = getStatusMeta(entry.status);
            return (
              <div key={entry.userId} className="flex gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                {entry.avatarUrl ? (
                  <img src={entry.avatarUrl} alt={entry.displayName} className="h-11 w-11 rounded-2xl object-cover" />
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-fuchsia-500/20 text-sm font-semibold text-fuchsia-100">
                    {getEntryInitial(entry)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="truncate text-sm font-medium text-white">{entry.displayName}</div>
                    <span className={`rounded-full border px-2.5 py-1 text-xs ${statusMeta.className}`}>
                      {statusMeta.label}
                    </span>
                  </div>
                  {entry.note && <div className="mt-2 text-sm leading-6 text-zinc-300">{entry.note}</div>}
                  <div className="mt-2 text-xs text-zinc-500">
                    更新时间：{new Date(entry.updatedAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
    );
  };

  const renderRules = () => (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-[24px] border border-sky-400/20 bg-zinc-950/80">
        <div className="grid gap-0 lg:grid-cols-[minmax(280px,0.9fr)_minmax(0,1.1fr)]">
          <div className="border-b border-white/10 lg:border-b-0 lg:border-r lg:border-white/10">
            <div
              className="relative aspect-[717/893] h-full min-h-[420px] overflow-hidden bg-sky-950/30 touch-pan-y"
              onPointerDown={handleRulesPosterPointerDown}
              onPointerMove={handleRulesPosterPointerMove}
              onPointerUp={handleRulesPosterPointerEnd}
              onPointerCancel={handleRulesPosterPointerEnd}
            >
              <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-sky-950/40">
                <img
                  key={KWC_RULE_POSTER_ITEMS[activeRulesPosterIndex].alt}
                  src={KWC_RULE_POSTER_ITEMS[activeRulesPosterIndex].src}
                  alt={KWC_RULE_POSTER_ITEMS[activeRulesPosterIndex].alt}
                  draggable={false}
                  className="pointer-events-none h-full w-full select-none object-contain"
                />
              </div>

              <button
                type="button"
                aria-label="上一张规则海报"
                onClick={() => scrollRulesPosterToIndex(activeRulesPosterIndex - 1)}
                className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/35 text-white/80 backdrop-blur transition hover:bg-black/50 hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="下一张规则海报"
                onClick={() => scrollRulesPosterToIndex(activeRulesPosterIndex + 1)}
                className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/35 text-white/80 backdrop-blur transition hover:bg-black/50 hover:text-white"
              >
                <ChevronRight className="h-4 w-4" />
              </button>

              <div className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-2 px-4">
                {KWC_RULE_POSTER_ITEMS.map((item, index) => (
                  <button
                    key={item.alt}
                    type="button"
                    aria-label={`查看第 ${index + 1} 张规则海报`}
                    onClick={() => scrollRulesPosterToIndex(index)}
                    className={`h-2.5 rounded-full transition ${
                      activeRulesPosterIndex === index ? 'w-7 bg-white' : 'w-2.5 bg-white/45 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <div className="mt-4 text-2xl font-semibold text-white">规则重点</div>
            <div className="mt-2 text-sm leading-6 text-sky-100/75">1.本次KWC没有备选招式</div>
            <div className="mt-5 rounded-2xl border border-white/8 bg-white/[0.03] p-4 sm:p-5">
              <div className="space-y-3 text-sm leading-7 text-zinc-200">
                {KWC_PRELIM_RULE_POINTS.map((item) => (
                  <p key={item}>{item}</p>
                ))}
              </div>
            </div>
            <div className="mt-4 text-sm leading-6 text-sky-100/75">2.剑玉尺寸规定</div>
            <div className="mt-3 rounded-2xl border border-white/8 bg-white/[0.03] p-4 sm:p-5">
              <div className="space-y-3 text-sm leading-7 text-zinc-200">
                <p>剑玉高度上限：175mm</p>
                <p>玉孔直径上限：26.5mm</p>
                <p>以上为允许使用的最大尺寸，请大家按此标准准备比赛用剑玉。</p>
                <p>剑玉检查会在广岛廿日市会场完成报名后进行。</p>
                <p>我们理解大家可能尤其关心玉孔尺寸，因此现场将按照以下原则进行检查。</p>
                <p>即使剑玉制作标称为 26.5mm，实际生产中也难免会有轻微误差。</p>
                <p>另外，现场测量为人工操作，也可能存在一定误差。</p>
                <p>综合以上情况，现场测量结果只要显示在 27mm 以内，就会判定为可使用，并贴上检验贴纸。</p>
                <p>如果超过这个尺寸，则不能在比赛中使用。</p>
                <p>感谢理解。</p>
              </div>
            </div>
            <div className="mt-4 text-sm leading-6 text-sky-100/75">3.第二日决赛招式以及奖励分</div>
            <div className="mt-3 rounded-2xl border border-white/8 bg-white/[0.03] p-4 sm:p-5">
              <div className="space-y-3 text-sm leading-7 text-zinc-200">
                <p>决赛招式等级 Levels: 4-15</p>
                <p>奖励分：</p>
                <p>13级及其以上招式为乘算分加对应等级的奖励分。</p>
                <p>Level 13 → 169 + 30 = 199 pts</p>
                <p>Level 14 → 196 + 100 = 296 pts</p>
                <p>Level 15 → 225 + 225 = 450 pts</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[24px] border border-white/10 bg-zinc-950/80 p-5">
        <div className="text-xl font-semibold text-white">官方入口</div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            { title: 'KWC 官方首页', href: KWC_OFFICIAL_HOME_URL },
            { title: 'KWC 官方规则 PDF', href: KWC_OFFICIAL_RULES_URL },
            { title: 'KWC 官方报名规则', href: KWC_OFFICIAL_ENTRY_URL }
          ].map((item) => (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition hover:border-white/20 hover:bg-white/[0.05]"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-white">{item.title}</div>
                <ExternalLink className="h-4 w-4 text-white/45 transition group-hover:text-white/80" />
              </div>
            </a>
          ))}
        </div>
        <div className="mt-4 text-sm leading-6 text-zinc-400">{KWC_GLOKEN_ASSET_NOTICE}</div>
      </section>
    </div>
  );

  const renderTravel = () => (
    <div className="space-y-4">
      <section className="rounded-[24px] border border-emerald-400/15 bg-zinc-950/80 p-5">
        <div className="flex items-center gap-2 text-xl font-semibold text-white">
          <Flag className="h-5 w-5 text-emerald-300" />
          日本签证办理
        </div>
        <div className="mt-4 space-y-3 text-sm leading-7 text-zinc-300">
          <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
            目前中国公民在中国境内申请赴日签证，需按常住地对应的领区办理；旅游签证原则上通过日本驻华使领馆指定旅行社或签证代办机构提交。
          </div>
          <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
            单次旅游电子签证主要适用于 30 天内旅游目的短期停留，具体是否适用以及材料细节，请直接以所在领区的日本使领馆说明为准。
          </div>
          <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
            建议准备顺序：先确认护照有效期与空白页，再根据领区找指定代办，随后准备行程单、住宿预订单、财力/在职等材料。
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <a href={JAPAN_VISA_QA_URL} target="_blank" rel="noreferrer" className="rounded-full border border-emerald-300/40 bg-emerald-500/15 px-4 py-2 text-sm text-emerald-100 transition hover:bg-emerald-500/25">
            查看签证 Q&A
          </a>
          <a href={JAPAN_VISA_TRAVEL_AGENCY_URL} target="_blank" rel="noreferrer" className="rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white">
            查看指定旅行社
          </a>
        </div>
      </section>

      <section className="rounded-[24px] border border-cyan-400/15 bg-zinc-950/80 p-5">
        <div className="flex items-center gap-2 text-xl font-semibold text-white">
          <MapPinned className="h-5 w-5 text-cyan-300" />
          比赛会场与交通
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4 text-sm leading-7 text-zinc-300">
            <div className="font-medium text-white">会场</div>
            <div className="mt-2">GLOBAL RESORT sports center Suncherry</div>
            <div>6-1-1 Kushido, Hatsukaichi, Hiroshima, Japan</div>
          </div>
          <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4 text-sm leading-7 text-zinc-300">
            <div className="font-medium text-white">官方交通信息</div>
            <div className="mt-2">JR 山阳本线 Miyauchi-Kushido 站步行约 10 分钟</div>
            <div>广电铁宫岛线 Hatsukaichi-shiyakusho-mae / Miyauchi 站步行约 10 分钟</div>
          </div>
        </div>
      </section>

      <section className="rounded-[24px] border border-violet-400/15 bg-zinc-950/80 p-5">
        <div className="text-xl font-semibold text-white">住宿建议</div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            {
              title: '廿日市 / 宫内附近',
              description: '离会场最近，适合比赛日通勤最省心。房量通常不多，建议尽早订。'
            },
            {
              title: '宫岛口 / Miyajimaguchi',
              description: '方便顺带去宫岛观光，适合把比赛和旅行拼在一起。'
            },
            {
              title: '广岛站周边',
              description: '交通、餐饮和住宿选择最多，适合第一次去广岛或同行人数较多。'
            }
          ].map((item) => (
            <div key={item.title} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4 text-sm leading-6 text-zinc-300">
              <div className="font-medium text-white">{item.title}</div>
              <div className="mt-2">{item.description}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-[24px] border border-white/10 bg-zinc-950/80 p-5">
        <div className="text-xl font-semibold text-white">周边旅游建议</div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {[
            '宫岛 / 严岛神社：最适合赛前或赛后半日到一日游。',
            '广岛和平纪念公园：从广岛市区出发较方便，适合安排一整天。',
            '廿日市本地商圈：适合比赛当天结束后简单吃饭和补给。',
            '如果住广岛站周边，可以把比赛日和市区观光分开安排，节奏更轻松。'
          ].map((item) => (
            <div key={item} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4 text-sm leading-6 text-zinc-300">
              {item}
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  const renderContent = () => {
    switch (page) {
      case 'tricks':
        return renderTricks();
      case 'teamup':
        return renderTeamup();
      case 'rules':
        return renderRules();
      case 'travel':
        return renderTravel();
      default:
        return renderHub();
    }
  };

  return (
    <div className="relative isolate min-h-full w-full overflow-y-auto text-white">
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(76,29,149,0.28),transparent_32%),linear-gradient(180deg,#05070d_0%,#090b12_45%,#05070d_100%)]" />
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 pb-24 pt-5 sm:px-6 lg:px-8">
        <header className="sticky top-0 z-20 rounded-[26px] border border-white/10 bg-black/45 p-4 backdrop-blur-md">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={page === 'hub' ? onBack : () => navigate('/kwc2026')}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/[0.04] text-white/85 transition hover:border-white/25 hover:text-white"
                aria-label="返回"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-violet-200/70">KWC2026</div>
                <h1 className="text-xl font-semibold text-white sm:text-2xl">{PAGE_TITLE_MAP[page]}</h1>
              </div>
            </div>

            {page !== 'hub' && (
              <button
                type="button"
                onClick={onBack}
                className="rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white"
              >
                返回首页
              </button>
            )}
          </div>
        </header>

        {renderContent()}
      </div>

      {isSignupModalOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={closeSignupModal}
        >
          <div
            className="w-full max-w-md rounded-[28px] border border-white/12 bg-zinc-950/95 p-5 shadow-[0_0_50px_rgba(0,0,0,0.45)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-violet-200/70">KWC2026</div>
                <h2 className="mt-2 text-xl font-semibold text-white">报名入口</h2>
                <p className="mt-2 text-sm leading-6 text-zinc-400">
                  请选择你要进入的报名方式。
                </p>
              </div>
              <button
                type="button"
                onClick={closeSignupModal}
                className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white"
              >
                关闭
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {KWC_SIGNUP_OPTION_ITEMS.map((item) => (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => {
                    openExternalLink(item.href);
                    closeSignupModal();
                  }}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-4 text-left text-white transition hover:border-white/25 hover:bg-white/[0.08]"
                >
                  <span className="text-sm leading-6">{item.title}</span>
                  <ExternalLink className="h-4 w-4 shrink-0 text-white/70" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {isTeamupFormOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={() => setIsTeamupFormOpen(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-[28px] border border-white/12 bg-zinc-950/95 p-5 shadow-[0_0_50px_rgba(0,0,0,0.45)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-violet-200/70">KWC2026</div>
                <h2 className="mt-2 text-xl font-semibold text-white">投票</h2>
                <p className="mt-2 text-sm leading-6 text-zinc-400">
                  选择你的状态，留言可选填，最多 30 字。
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsTeamupFormOpen(false)}
                className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white"
              >
                关闭
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTeamupStatus(option.value)}
                  className={`w-full rounded-[18px] border p-4 text-left transition ${
                    teamupStatus === option.value
                      ? 'border-fuchsia-300 bg-fuchsia-500/15 text-white shadow-[0_0_24px_rgba(217,70,239,0.16)]'
                      : 'border-white/10 bg-white/[0.03] text-zinc-300 hover:border-white/20 hover:text-white'
                  }`}
                >
                  <div className="text-base font-medium">{option.label}</div>
                  <div className="mt-1 text-sm leading-6 text-zinc-300">{option.description}</div>
                </button>
              ))}
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-sm text-zinc-300" htmlFor="kwc-teamup-note">
                留言（可选）
              </label>
              <textarea
                id="kwc-teamup-note"
                value={teamupNote}
                onChange={(event) => setTeamupNote(Array.from(event.target.value).slice(0, 30).join(''))}
                rows={3}
                placeholder="比如：想拼房 / 从广州出发 / 想约一起练招"
                className="w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition placeholder:text-zinc-500 focus:border-fuchsia-300/70"
              />
              <div className="mt-2 text-right text-xs text-zinc-500">{Array.from(teamupNote).length} / 30</div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void handleSaveTeamup()}
                disabled={isSaving}
                className="rounded-full border border-fuchsia-300/50 bg-fuchsia-500/20 px-5 py-2.5 text-sm text-fuchsia-100 transition hover:bg-fuchsia-500/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? '提交中...' : (myEntry ? '更新状态' : '参加活动')}
              </button>
              {!user && (
                <button
                  type="button"
                  onClick={() => onRequireLogin('请先登录后参加 KWC 找队友活动。')}
                  className="rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white"
                >
                  先登录
                </button>
              )}
              {actionMessage && <div className="text-sm text-zinc-300">{actionMessage}</div>}
            </div>
          </div>
        </div>
      )}

      {isLotteryOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={closeLottery}
        >
          <div
            className="w-full max-w-md rounded-[28px] border border-amber-300/20 bg-zinc-950/95 p-5 shadow-[0_0_50px_rgba(0,0,0,0.45)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-200/70">KWC2026</div>
                <h2 className="mt-2 text-xl font-semibold text-white">抽奖</h2>
                <p className="mt-2 text-sm leading-6 text-zinc-400">
                  从活动列表的所有参与者中随机抽选一位。
                </p>
              </div>
              <button
                type="button"
                onClick={closeLottery}
                className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white"
              >
                关闭
              </button>
            </div>

            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              {lotteryWinner ? (
                <div className="flex items-center gap-3">
                  {lotteryWinner.avatarUrl ? (
                    <img src={lotteryWinner.avatarUrl} alt={lotteryWinner.displayName} className="h-14 w-14 rounded-2xl object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/20 text-lg font-semibold text-amber-100">
                      {getEntryInitial(lotteryWinner)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-semibold text-white">{lotteryWinner.displayName}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-xs ${getStatusMeta(lotteryWinner.status).className}`}>
                        {getStatusMeta(lotteryWinner.status).label}
                      </span>
                    </div>
                    {lotteryWinner.note && <div className="mt-2 text-sm leading-6 text-zinc-300">{lotteryWinner.note}</div>}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <Trophy className="h-8 w-8 text-amber-200/60" />
                  <div className="mt-3 text-sm text-zinc-400">
                    {entries.length === 0 ? '还没有参与者' : '点击下方按钮开始抽奖'}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={startLottery}
                disabled={isLotteryDrawing || entries.length === 0}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-amber-300/50 bg-amber-500/20 px-5 py-2.5 text-sm text-amber-100 transition hover:bg-amber-500/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Trophy className="h-4 w-4" />
                {isLotteryDrawing ? '抽选中...' : '抽奖'}
              </button>
            </div>

            <div className="mt-3 text-center text-xs text-zinc-500">共 {entries.length} 人参与</div>
          </div>
        </div>
      )}

      {isPrizePoolOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={() => setIsPrizePoolOpen(false)}
        >
          <div
            className="max-h-[88vh] w-full max-w-xl overflow-y-auto rounded-[28px] border border-sky-300/20 bg-zinc-950/95 p-5 shadow-[0_0_50px_rgba(0,0,0,0.45)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-sky-200/70">KWC2026</div>
                <h2 className="mt-2 text-xl font-semibold text-white">抽奖池</h2>
                <p className="mt-2 text-sm leading-6 text-zinc-400">
                  当前抽奖活动奖品展示。活动结束后开始抽奖。
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPrizePoolOpen(false)}
                className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white"
              >
                关闭
              </button>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              {KWC_TEAMUP_LOTTERY_PRIZES.map((prize) => (
                <div
                  key={prize.src}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]"
                >
                  <button
                    type="button"
                    onClick={() => setPreviewPrize(prize)}
                    className="block w-full"
                  >
                    <div className="aspect-[3/4] overflow-hidden bg-black/20">
                      <img
                        src={prize.src}
                        alt={prize.alt}
                        className="h-full w-full object-cover transition hover:scale-105"
                      />
                    </div>
                  </button>
                  <div className="px-3 py-2 text-center text-xs text-zinc-300">
                    {prize.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {previewPrize && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
          onClick={() => setPreviewPrize(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-[28px] border border-sky-300/20 bg-zinc-950/95 p-4 shadow-[0_0_50px_rgba(0,0,0,0.5)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-zinc-300">{previewPrize.label}</div>
              <button
                type="button"
                onClick={() => setPreviewPrize(null)}
                className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-zinc-200 transition hover:border-white/25 hover:text-white"
              >
                关闭
              </button>
            </div>
            <div className="mt-4 flex items-center justify-center overflow-hidden rounded-2xl bg-black/20">
              <img
                src={previewPrize.src}
                alt={previewPrize.alt}
                className="max-h-[75vh] w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KWC2026ActivityMode;
